import { CacheBucket, withCache } from '@ngneat/cashew';
import { EMPTY, Observable, expand, reduce, tap } from 'rxjs';

import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '@environments/environment';

import { API } from '@models/API/index';
import { Operation } from '@models/operation';

import { platformAPI } from '@services/API/endpoints';
import { OperationsState } from '@services/state/operation-state/operation.state';

@Injectable({ providedIn: 'root' })
export class OperationsAPI {
  /**
   * Cache key PREFIX for the operations list. The real key appends the
   * request's own `urlWithParams` (see `fetchOperationsPage`).
   *
   * A bare constant here was a bug (LEN-2158): given an explicit `key`, cashew
   * returns it verbatim and never looks at `urlWithParams` --
   *
   *     const { key } = context;
   *     if (key) { return (...).toString(); }
   *     return request.urlWithParams;
   *
   * -- so every `getOperations` call shared ONE entry for the whole TTL, even
   * though `operationGuard` always filters by a single `trust_number`. Opening
   * a second facility within 5 minutes was served the first one's response,
   * found no matching trust in it, and answered with the "Facility created."
   * toast while blocking the navigation.
   */
  public cacheKey = 'trusts-operations';

  /**
   * Groups every per-URL entry above, so a mutation can still drop the whole
   * catalogue in one call. `HttpCacheManager.delete()` takes a key or a bucket;
   * with keys that vary per request, deleting by name would now miss them all.
   */
  public cacheBucket = new CacheBucket();

  constructor(
    private http: HttpClient,
    private operationsState: OperationsState
  ) {}

  getOperations(
    queryFilter?: Operation.QueryFilter,
    loaderOptIn: boolean | string = true
  ): Observable<API.Trusts.Operations.Response> {
    let params = new HttpParams();

    if (queryFilter) {
      const fields = Object.keys(
        queryFilter
      ) as (keyof Operation.QueryFilter)[];

      for (const field of fields) {
        const value = queryFilter[field];

        if (value && Array.isArray(value) && value.length > 0) {
          // Join array items into a comma-separated string
          params = params.set(field, value.join(','));
        }
      }
    }

    // size=100 with expand()-driven pagination below would issue N sequential
    // requests for a result set larger than one page. The only caller is
    // operationGuard, which always filters by a single trust_number, so the
    // second page is unreachable today. Revisit before calling this unfiltered.
    params = params.set('size', '100');

    return this.fetchOperationsPage(params, loaderOptIn).pipe(
      expand((response: API.Trusts.Operations.Response) => {
        if (response.page < response.pages) {
          return this.fetchOperationsPage(
            params.set('page', (response.page + 1).toString()),
            loaderOptIn
          );
        }
        return EMPTY;
      }),
      reduce(
        (
          acc: API.Trusts.Operations.Response,
          response: API.Trusts.Operations.Response
        ) => {
          return {
            ...response,
            items: [...acc.items, ...response.items],
          };
        }
      ),
      tap(response => {
        this.operationsState.set(response);
      })
    );
  }

  private fetchOperationsPage(
    params: HttpParams,
    loaderOptIn: boolean | string
  ): Observable<API.Trusts.Operations.Response> {
    const url = platformAPI.trusts().operations;

    return this.http.get<API.Trusts.Operations.Response>(url, {
      params,
      headers: new HttpHeaders({
        'Loader-Opt-In': `${loaderOptIn}`,
        'Auth-Opt-In': 'true',
      }),
      // withCache was already imported for getContacts but never applied here,
      // so the guard paid the full cost on every cold entry -- and the backend
      // cost is proportional to the collection, not to the page.
      //
      // The key has to carry `urlWithParams`: the filter and the page number
      // live there, and two calls that ask different questions must not share
      // an answer (LEN-2158).
      context: withCache({
        ttl: environment.CACHE_LIFETIME!,
        key: (request: HttpRequest<unknown>) =>
          `${this.cacheKey}-${request.urlWithParams}`,
        bucket: this.cacheBucket,
      }),
    });
  }

  getContacts(
    trustNumber: string,
    loaderOptIn: boolean | string = true
  ): Observable<API.Trusts.Operations.Contacts.Response> {
    const params = new HttpParams().set(
      'priority',
      this.operationsState.selectedPriority()
    );

    return this.http.get<API.Trusts.Operations.Contacts.Response>(
      platformAPI.trusts(trustNumber).operations.directory,
      {
        headers: new HttpHeaders({
          'Loader-Opt-In': `${loaderOptIn}`,
          'Auth-Opt-In': 'true',
        }),
        params,
        context: withCache({
          ttl: environment.CACHE_LIFETIME!,
        }),
      }
    );
  }
}
